import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';

import { Slider } from '@atlas/components';
import { SiteConfigManager, useApplicationState, useSettings } from '@atlas/core';
import { CASINO_TILE_TYPES, Label } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import { CDN_TYPE_CMS, CDN_TYPE_GAME, imageUrlCDN } from 'common/utils/utils';
import { useIsRtl } from 'hooks/useIsRtl';
import requestBuilder from 'src/common/utils/requestBuilder';
import { getGameIdentifier } from 'src/helpers/casino';

import InfoGame from '../GameList/GameTile';

const LobbyRecentlyPlayedGamesBlock = ({ blockTitleKey, imageUrl }) => {
  const [isShowImg, toggleVisibilityImg] = useState(true);
  const handleError = useCallback(() => toggleVisibilityImg(false), []);

  const { authenticated } = useApplicationState();
  const { lang } = useSettings();

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  const casinoGameTileType = SiteConfigManager.getConfig(
    'config.config.general.mobileCasinoGameTileView',
    CASINO_TILE_TYPES?.TYPE_5 || 'type_5',
  );
  const tileTypeClass = {
    'lobby-recently-game__slider-type_1': casinoGameTileType === CASINO_TILE_TYPES?.TYPE_1,
    'lobby-recently-game__slider-type_2': casinoGameTileType === CASINO_TILE_TYPES?.TYPE_2,
    'lobby-recently-game__slider-type_3': casinoGameTileType === CASINO_TILE_TYPES?.TYPE_3,
    'lobby-recently-game__slider-type_4': casinoGameTileType === CASINO_TILE_TYPES?.TYPE_4,
    'lobby-recently-game__slider-type_5': casinoGameTileType === CASINO_TILE_TYPES?.TYPE_5,
    'lobby-recently-game__slider-type_6': casinoGameTileType === CASINO_TILE_TYPES?.TYPE_6,
  };
  const spaceBetween = casinoGameTileType === (CASINO_TILE_TYPES?.TYPE_2 || 'type_2') ? 4 : 8;

  const [fetchedGames, setFetchedGames] = useState(null);
  const [gameOverlayId, setGameOverlayId] = useState(null);

  const toggleShowOverlay = useCallback(game => {
    const gameIdentifier = getGameIdentifier(game);
    setGameOverlayId(prevGameOverlayId =>
      prevGameOverlayId !== gameIdentifier ? gameIdentifier : null,
    );
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setFetchedGames([]);
      return;
    }

    const getGamesHistory = requestBuilder.create('/api/gs/recently-played', { method: 'GET' });
    getGamesHistory
      .execute({ query: { locale: lang } })
      .then(response => {
        const games = Array.isArray(response) ? response : response?.games || [];
        setFetchedGames(games.slice(0, 10));
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch recently played games', err);
        setFetchedGames([]);
      });
  }, [authenticated, lang]);

  const finalItems = useMemo(() => {
    if (!fetchedGames) {
      return [];
    }

    return fetchedGames.map(game => ({
      finalImageUrl: game.iconUrl ? imageUrlCDN(game.iconUrl, CDN_TYPE_GAME) : null,
      gId: game.id,
      tileGame: {
        ...game,
        providerName: game.providerName || game.providerDisplayedName,
      },
    }));
  }, [fetchedGames]);

  // Hide the block if user is not authenticated, games are loading, or user has no recently played games
  if (!authenticated || !fetchedGames || fetchedGames.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-recently-game">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={24}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/recent-played-games.png`
                }
                width={24}
              />
            </div>
          )}

          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
      </div>
      <div className="casino-lobby__content">
        <div className={cx('lobby-recently-game__slider', tileTypeClass)}>
          <Slider
            autoplay={false}
            breakpointsBase="container"
            dir={dir}
            direction="horizontal"
            loop={false}
            slidesPerView={'auto'}
            spaceBetween={spaceBetween}
          >
            {finalItems.map(({ finalImageUrl, gId, tileGame }) => {
              const gameIdentifier = getGameIdentifier(tileGame);
              const showOverlay = gameOverlayId === gameIdentifier;

              return (
                <Slider.Slide key={gId}>
                  <InfoGame
                    game={tileGame}
                    imageOverride={finalImageUrl}
                    isLoading={false}
                    showOverlay={showOverlay}
                    size="S"
                    toggleShowOverlay={toggleShowOverlay}
                  />
                </Slider.Slide>
              );
            })}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyRecentlyPlayedGamesBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  imageUrl: PropTypes.string,
};

export default LobbyRecentlyPlayedGamesBlock;
